import { IJadwaOrder, IJadwaTransfer, JadwaTransferStatus } from '../../../../types/jadwa';

/**
 * madkhol-jadwa keeps both managers' rows (`order.fund_manager`, NOT NULL, backfilled to JADWA), and the store holds
 * them all — the Jadwa tab shows only these.
 */
export const JADWA_FUND_MANAGER = 'JADWA';
/** Albilad — a second manager on madkhol-jadwa; its tab reuses the Jadwa tab filtered to these rows. */
export const ALBILAD_FUND_MANAGER = 'ALBILAD';
/** SNB Capital — a third manager on madkhol-jadwa; its tab reuses the Jadwa tab filtered to these rows. */
export const SNB_FUND_MANAGER = 'SNB';

export const filterJadwaManagerOrders = (
  orders: IJadwaOrder[],
  fundManager: string = JADWA_FUND_MANAGER,
): IJadwaOrder[] => orders.filter((order) => order.fundManager === fundManager);

/** One wire of a Madkhol order, with the fund orders (and their tab index) it carries. */
export interface JadwaOrderTransferSummary {
  transfer: IJadwaTransfer;
  fundOrders: { fundOrderId: number; tabIndex: number }[];
}

/**
 * Every transfer of a Madkhol order, once each. madkhol-jadwa wires one transfer per currency and links each fund
 * order to its own currency's transfer, so the transfers are spread across the fund-order rows — a USD fund order
 * never shows the SAR wire.
 */
export const getJadwaOrderTransfers = (orders: IJadwaOrder[]): JadwaOrderTransferSummary[] => {
  const byTransferId = new Map<number, JadwaOrderTransferSummary>();

  orders.forEach((order, tabIndex) => {
    (order.transfers ?? []).forEach((transfer) => {
      const entry = byTransferId.get(transfer.id) ?? { transfer, fundOrders: [] };
      entry.fundOrders.push({ fundOrderId: order.fundOrderId, tabIndex });
      byTransferId.set(transfer.id, entry);
    });
  });

  return [...byTransferId.values()].sort((a, b) => a.transfer.id - b.transfer.id);
};

/** One side (client or satellite) of a fund order's money, in SAR. */
export interface JadwaAmountBreakdown {
  gross: number;
  fx: number;
  fee: number;
  /** gross − fx − fee: what is invested. */
  net: number;
}

/** What ops invests per fund order, after the FX and transfer fee came off the wire. */
export interface JadwaFundInvestAmount {
  fundOrderId: number;
  tabIndex: number;
  currency: string | null;
  channel: string | null;
  client: JadwaAmountBreakdown;
  satellite: JadwaAmountBreakdown;
  /** client.net + satellite.net, in SAR. */
  totalNetSar: number;
  /**
   * The same in the wire's currency — for a USD fund, this fund's exact share of the USD the wire sent
   * (transfer.amount × totalNetSar ÷ Σ net SAR of the wire). null when there is no wire or nothing to share.
   */
  totalNetInCurrency: number | null;
}

const toNumber = (value: unknown): number => Number(value) || 0;

/** A fund order's live wire: after a retry it also carries the old, RETRIED one, which no longer counts. */
const liveTransfer = (order: IJadwaOrder): IJadwaTransfer | undefined =>
  order.transfers?.find((transfer) => transfer.status !== JadwaTransferStatus.RETRIED) ?? order.transfers?.[0];

export const getJadwaAmountBreakdowns = (order: IJadwaOrder) => {
  const client: JadwaAmountBreakdown = {
    gross: toNumber(order.grossAmount),
    fx: toNumber(order.fxDeduction),
    fee: toNumber(order.transferFeeDeduction),
    net: toNumber(order.netAmount),
  };
  const satelliteFx = toNumber(order.satelliteFxDeduction);
  const satelliteFee = toNumber(order.satelliteTransferFeeDeduction);
  const satelliteNet = toNumber(order.satelliteNetAmount);
  // The listener does not fill satellite_gross_amount — gross is net + FX + fee then.
  const satellite: JadwaAmountBreakdown = {
    gross:
      order.satelliteGrossAmount != null
        ? toNumber(order.satelliteGrossAmount)
        : satelliteNet + satelliteFx + satelliteFee,
    fx: satelliteFx,
    fee: satelliteFee,
    net: satelliteNet,
  };

  return { client, satellite };
};

/** Every fund order of a Madkhol order with the amounts ops invests, in tab order. */
export const getJadwaFundInvestAmounts = (orders: IJadwaOrder[]): JadwaFundInvestAmount[] => {
  // Σ net SAR per wire — the denominator of each fund's share of the USD sent.
  const netSarByTransferId = new Map<number, number>();

  orders.forEach((order) => {
    const transfer = liveTransfer(order);

    if (transfer) {
      const { client, satellite } = getJadwaAmountBreakdowns(order);
      netSarByTransferId.set(transfer.id, (netSarByTransferId.get(transfer.id) ?? 0) + client.net + satellite.net);
    }
  });

  return orders.map((order, tabIndex) => {
    const transfer = liveTransfer(order);
    const { client, satellite } = getJadwaAmountBreakdowns(order);
    const totalNetSar = client.net + satellite.net;
    const wireNetSar = transfer ? netSarByTransferId.get(transfer.id) ?? 0 : 0;

    return {
      fundOrderId: order.fundOrderId,
      tabIndex,
      currency: transfer?.currency ?? null,
      channel: order.transferChannel ?? null,
      client,
      satellite,
      totalNetSar,
      totalNetInCurrency: transfer && wireNetSar > 0 ? (toNumber(transfer.amount) * totalNetSar) / wireNetSar : null,
    };
  });
};

/**
 * A Jadwa / Albilad transfer counts as done once an admin approved it (subscription) or the money came back and it
 * completed (redemption).
 */
export const JADWA_SETTLED_TRANSFER_STATUSES: JadwaTransferStatus[] = [
  JadwaTransferStatus.APPROVED,
  JadwaTransferStatus.COMPLETE,
];

/**
 * A wire that never reached the manager. The admin can't approve it (Update shows for QUEUED only) and madkhol-jadwa
 * creates no new wire for it by itself — only a FAILED one can be retried, on production (POST /transfer/:id/retry).
 */
export const JADWA_FAILED_TRANSFER_STATUSES: JadwaTransferStatus[] = [
  JadwaTransferStatus.REJECTED,
  JadwaTransferStatus.CANCELED,
  JadwaTransferStatus.RETURNED,
  JadwaTransferStatus.FAILED,
];

const describeTransfers = (transfers: IJadwaTransfer[]) =>
  transfers.map((transfer) => `#${transfer.id} (${transfer.currency}, ${transfer.status})`).join(', ');

/**
 * Why the execution file can't be exported / imported yet for an order with Jadwa or Albilad funds, or null when it
 * can: the manager's rows aren't there yet, a fund order has no transfer, a wire failed, or a wire isn't approved.
 *
 * A RETRIED transfer is ignored: the retry linked a new transfer to the same fund orders, and that one is checked.
 *
 * ⚠️ `expectedFundOrderIds` — every Jadwa / Albilad / SNB subscription fund order of the order. Jadwa, Albilad and
 * SNB can share an order and each manager runs on its own, so once the first manager's rows exist (and are approved)
 * the others may not have run yet. Checking only the rows that exist would open the file early.
 */
export const getJadwaExecutionFileBlockReason = (
  orders: IJadwaOrder[],
  expectedFundOrderIds: number[] = [],
): string | null => {
  if (!orders.length) {
    return 'The Jadwa / Albilad / SNB transfer is not created yet. Export and import open once it is created and approved.';
  }

  const fundOrderIdsWithRow = new Set(orders.map((order) => order.fundOrderId));

  if (expectedFundOrderIds.some((fundOrderId) => !fundOrderIdsWithRow.has(fundOrderId))) {
    return 'A fund manager (Jadwa / Albilad / SNB) on this order has not been triggered yet. Export and import open once every manager’s transfer is created and approved.';
  }

  if (orders.some((order) => !order.transfers?.some((transfer) => transfer.status !== JadwaTransferStatus.RETRIED))) {
    return 'A Jadwa / Albilad fund order has no transfer yet. Export and import open once every transfer is approved.';
  }

  const transfers = getJadwaOrderTransfers(orders)
    .map(({ transfer }) => transfer)
    .filter((transfer) => transfer.status !== JadwaTransferStatus.RETRIED);
  const failed = transfers.filter((transfer) => JADWA_FAILED_TRANSFER_STATUSES.includes(transfer.status));

  if (failed.length) {
    return `The Jadwa / Albilad transfer${failed.length > 1 ? 's' : ''} ${describeTransfers(failed)} never reached ${
      failed.length > 1 ? 'the managers' : 'the manager'
    } and can't be approved. A new wire is needed before the execution file can be exported or imported — ask tech (a FAILED wire can be retried on production).`;
  }

  const pending = transfers.filter((transfer) => !JADWA_SETTLED_TRANSFER_STATUSES.includes(transfer.status));

  if (!pending.length) {
    return null;
  }

  return `Approve the Jadwa / Albilad transfer${pending.length > 1 ? 's' : ''} ${describeTransfers(
    pending,
  )} before exporting or importing the execution file.`;
};

/** Chip colour per transfer status. */
export const JADWA_TRANSFER_STATUS_COLORS: Partial<
  Record<JadwaTransferStatus, 'default' | 'error' | 'info' | 'success' | 'warning'>
> = {
  [JadwaTransferStatus.QUEUED]: 'info',
  [JadwaTransferStatus.APPROVAL_PENDING]: 'warning',
  [JadwaTransferStatus.PENDING]: 'warning',
  [JadwaTransferStatus.SENT_TO_CLEARING]: 'info',
  [JadwaTransferStatus.APPROVED]: 'success',
  [JadwaTransferStatus.COMPLETE]: 'success',
  [JadwaTransferStatus.REJECTED]: 'error',
  [JadwaTransferStatus.CANCELED]: 'error',
  [JadwaTransferStatus.RETURNED]: 'error',
  [JadwaTransferStatus.FAILED]: 'error',
  [JadwaTransferStatus.RETRIED]: 'default',
};
